'use client'

import React, { useState, useEffect, useCallback, useRef } from 'react'
import Image from 'next/image'
import { motion } from 'framer-motion'
import { ArrowRight, ArrowLeft, Check } from 'lucide-react'
import { CosmicShader } from '../ui/cosmic-shader'
import { AnimatedTestimonials } from '../ui/animated-testimonials'
import { TestimonialsColumn } from '../ui/testimonials-column'
import { TextLoop } from '../ui/text-loop'
import { IntroAnimation } from '../ui/intro-animation'
import { BlurTextEffect } from '../ui/blur-text-effect'
import { AnimatedBiomoLogo } from '../ui/animated-biomo-logo'
import { trackCtaClick } from '@/lib/analytics/rastreamento'

interface HeroLandingBProps {
  onStart: () => void
}

// Todos os projetos do portfólio para o carrossel animado
const animatedProjects = [
  {
    quote: 'Site elegante para barbearia premium com sistema de agendamento online e galeria de trabalhos.',
    name: 'Orestes Cabeleireiro',
    designation: 'Beleza & Estética',
    src: '/portfolio/orestes-cabeleireiro.webp',
    url: 'https://orestescabeleireiro.com.br',
  },
  {
    quote: 'Site institucional completo com otimização para SEO e integração com Google Ads para campanhas de tráfego pago.',
    name: 'Liboni Bauer',
    designation: 'Marketing Digital',
    src: '/portfolio/liboni-bauer.webp',
    url: 'https://libonibauer.com.br',
  },
  {
    quote: 'Landing page de alta conversão para clube de futebol com sistema de vendas de ingressos e área de sócios.',
    name: 'Tupã FC',
    designation: 'Esportes',
    src: '/portfolio/tupa-fc.webp',
    url: 'https://tupafc.com.br',
  },
  {
    quote: 'Site institucional profissional para empresa de engenharia com portfólio de projetos e orçamento online.',
    name: 'Prospec Engenharia',
    designation: 'Engenharia',
    src: '/portfolio/prospec-engenharia.webp',
    url: 'https://prospecengenharia.com.br',
  },
  {
    quote: 'Plataforma de desenvolvimento pessoal com área de membros e conteúdo exclusivo.',
    name: 'Desafios do Pai',
    designation: 'Desenvolvimento Pessoal',
    src: '/portfolio/desafios-do-pai.webp',
    url: 'https://desafiosdopai.com.br',
  },
  {
    quote: 'Site para oficina automotiva com catálogo de serviços e agendamento de revisões.',
    name: 'Ponto do Carro',
    designation: 'Automotivo',
    src: '/portfolio/ponto-do-carro.webp',
    url: 'https://pontodocarro.com.br',
  },
  {
    quote: 'Portfólio profissional com design moderno e animações interativas.',
    name: 'Natan Cardeal',
    designation: 'Portfólio',
    src: '/portfolio/natan-cardeal.webp',
    url: 'https://natancardeal.com',
  },
  {
    quote: 'Site corporativo para empresa de tecnologia com soluções em TI e suporte técnico.',
    name: 'LITE TI',
    designation: 'Tecnologia',
    src: '/portfolio/lite-ti.webp',
    url: 'https://liteti.com.br',
  },
  {
    quote: 'Landing page premium para clínica de medicina estética com agendamento e galeria de resultados.',
    name: 'Instituto Med Menosso',
    designation: 'Medicina Estética',
    src: '/portfolio/instituto-med-menosso.webp',
    url: 'https://institutomedmenosso.com.br',
  },
  {
    quote: 'Site institucional para empresa de bioengenharia com foco em soluções ambientais sustentáveis.',
    name: 'TerraGreen',
    designation: 'Bioengenharia',
    src: '/portfolio/terragreen.webp',
    url: 'https://terragreen.com.br',
  },
  {
    quote: 'Portal corporativo para grupo empresarial com múltiplas unidades de negócio.',
    name: 'Grupo Costa Leste',
    designation: 'Grupo Empresarial',
    src: '/portfolio/grupo-costa-leste.webp',
    url: 'https://grupocostaleste.com.br',
  },
  {
    quote: 'Plataforma de serviços com sistema de orçamento e agendamento online.',
    name: 'SOS Diarista',
    designation: 'Serviços',
    src: '/portfolio/sos-diarista.webp',
    url: 'https://sosdiarista.com.br',
  },
  {
    quote: 'Site premium para serviços de limpeza com diferencial de qualidade e atendimento VIP.',
    name: 'Diarista VIP',
    designation: 'Serviços Premium',
    src: '/portfolio/diarista-vip.webp',
    url: 'https://diaristavip.com.br',
  },
  {
    quote: 'Portfólio sofisticado para estúdio de arquitetura com galeria de projetos em alta resolução.',
    name: 'Eliore Studio',
    designation: 'Arquitetura',
    src: '/portfolio/eliore-studio.webp',
    url: 'https://eliorestudio.com.br',
  },
  {
    quote: 'E-commerce de moda com catálogo de produtos e integração com meios de pagamento.',
    name: 'Mondelle Brasil',
    designation: 'Moda & Lifestyle',
    src: '/portfolio/mondelle-brasil.webp',
    url: 'https://mondellebrasil.com.br',
  },
  {
    quote: 'Site B2B industrial com catálogo técnico de produtos e área de representantes.',
    name: 'Homepar',
    designation: 'Industrial B2B',
    src: '/portfolio/homepar.webp',
    url: 'https://homepar.com.br',
  },
  {
    quote: 'Site institucional para empresa de comunicação visual com portfólio de trabalhos.',
    name: 'Litoral Comunicação',
    designation: 'Comunicação Visual',
    src: '/portfolio/litoral-comunicacao.webp',
    url: 'https://litoralcomunicacao.com.br',
  },
  {
    quote: 'Landing page especializada em harmonização facial com antes e depois dos procedimentos.',
    name: 'Harmonização Facial',
    designation: 'Estética',
    src: '/portfolio/harmonizacao-facial.webp',
    url: 'https://facial.institutomedmenosso.com.br',
  },
  {
    quote: 'Landing page focada em harmonização glútea com resultados e depoimentos de clientes.',
    name: 'Harmonização Glútea',
    designation: 'Estética',
    src: '/portfolio/harmonizacao-glutea.webp',
    url: 'https://gluteo.institutomedmenosso.com.br',
  },
  {
    quote: 'Site para distribuidora de gás com sistema de pedidos online e área de cobertura.',
    name: 'Ultragás Itajaí',
    designation: 'Utilities',
    src: '/portfolio/ultragas-itajai.webp',
    url: 'https://ultragasitajai.com.br',
  },
  {
    quote: 'Site para distribuidora de gás e água com catálogo de produtos e pedidos pelo WhatsApp.',
    name: 'Marques Gás e Água',
    designation: 'Utilities',
    src: '/portfolio/marques-gas-agua.webp',
    url: 'https://marquesgaseagua.com.br',
  },
]

// Dados para as colunas de depoimentos
const columnTestimonials = [
  // Coluna 1
  {
    text: 'Trabalhar com a Biomo transformou nosso faturamento este ano. O diferencial deles é a transparência com os dados.',
    name: 'Rodrigo Venturi',
    role: 'Empresário',
    initials: 'RV',
  },
  {
    text: 'Melhor agência de Balneário Camboriú e região, o atendimento do Thiago é fantástico!',
    name: 'Natan Cardeal',
    role: 'Cliente',
    initials: 'NC',
  },
  {
    text: 'A galera manda bem, entregando além do prometido, serviço rápido e de excelência.',
    name: 'Sidney Rodrigues',
    role: 'Empresário',
    initials: 'SR',
  },
  // Coluna 2
  {
    text: 'Equipe muito eficiente e entrega excelente resultado. Muito satisfeito!',
    name: 'Dr. João Reis',
    role: 'Médico',
    initials: 'JR',
  },
  {
    text: 'Se tivesse como colocaria 10 estrelas!!',
    name: 'Douglas Rodrigues',
    role: 'Cliente',
    initials: 'DR',
  },
  {
    text: 'Contratei mais de 4 empresas que não geravam resultado. A Biomo entregou!',
    name: 'Cesar Oliveira',
    role: 'Imobiliária BC',
    initials: 'CO',
  },
  // Coluna 3
  {
    text: 'Já fiz 2 sites e nunca me arrependi! Os sites ficam maravilhosos!',
    name: 'Talita Callahan',
    role: 'Empresária',
    initials: 'TC',
  },
  {
    text: 'Excelente profissional, dedicado, interessado em entregar o melhor!',
    name: 'Dieison Corazza',
    role: 'Cliente',
    initials: 'DC',
  },
  {
    text: 'Resultados gerados através do trabalho muito significativos, super recomendo.',
    name: 'Natan Cardeal',
    role: 'Cliente',
    initials: 'NC',
  },
]

const firstColumn = columnTestimonials.slice(0, 3)
const secondColumn = columnTestimonials.slice(3, 6)
const thirdColumn = columnTestimonials.slice(6, 9)

// Steps data for Como Funciona
const processSteps = [
  {
    metric: '2 min',
    title: 'Conte sua ideia',
    description: 'Responda sobre seu negócio, suas ideias e referências de sites que você gosta.',
    footer: 'Começar agora',
    isClickable: true,
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
      </svg>
    ),
    color: 'purple',
  },
  {
    metric: '24h',
    title: 'Proposta clara',
    description: 'Receba um orçamento detalhado com preço e prazo definidos. Sem surpresas no final.',
    footer: 'Orçamento sem compromisso',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
    color: 'purple',
  },
  {
    metric: '7 dias',
    title: 'Criação profissional',
    description: 'Desenvolvemos seu site com design exclusivo e otimizado para resultados.',
    footer: 'Não gostou? Não paga',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
      </svg>
    ),
    color: 'purple',
  },
  {
    metric: 'Pronto!',
    title: 'Site no ar',
    description: 'Seu site publicado e gerando resultados 24 horas por dia, 7 dias por semana.',
    footer: 'Suporte incluso',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
      </svg>
    ),
    color: 'emerald',
  },
]

function ComoFuncionaSection({ onStart }: { onStart: () => void }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const [inView, setInView] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    setIsMobile(
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 0 && window.innerWidth < 768)
    )
  }, [])

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true)
    }, { threshold: 0.3 })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  const handleNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % processSteps.length)
  }, [])

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + processSteps.length) % processSteps.length)
  }

  useEffect(() => {
    if (!inView) return
    const delay = currentIndex === 0 ? 10000 : 5000
    const timer = setTimeout(() => {
      handleNext()
    }, delay)
    return () => clearTimeout(timer)
  }, [inView, currentIndex, handleNext])

  return (
    <motion.section
      ref={sectionRef}
      id="como-funciona"
      initial={{ opacity: 0, y: isMobile ? 0 : 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: isMobile ? 0.3 : 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className="w-full py-10 sm:py-16 lg:py-24 scroll-mt-20 overflow-hidden"
    >
      <div className="container mx-auto px-6 lg:px-8">
        {/* Header - Title & Subtitle */}
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
          {/* Badge */}
          <div className="inline-flex mb-3 sm:mb-5">
            <div className="border border-indigo-950/20 dark:border-white/20 py-1 px-4 rounded-full text-indigo-950/70 dark:text-white/60 text-sm backdrop-blur-sm">
              Processo
            </div>
          </div>

          {/* Title */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-indigo-950 dark:text-white mb-3 sm:mb-4">
            Como funciona?{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-600 dark:from-purple-400 dark:to-purple-600">
              Simples e rápido
            </span>
          </h2>


          {/* Description */}
          <p className="text-indigo-950/70 dark:text-white/60 text-base lg:text-lg leading-relaxed">
            Do briefing ao site no ar em apenas 4 passos simples. Sem burocracia, sem enrolação.
          </p>
        </div>

        {/* 3D Carousel */}
        <div className="relative w-full h-[350px] md:h-[420px] flex items-center justify-center">
          {/* Carousel Wrapper */}
          <div className="relative w-full h-full flex items-center justify-center [perspective:1000px]">
            {processSteps.map((step, index) => {
              const offset = index - currentIndex
              const total = processSteps.length
              let pos = (offset + total) % total
              if (pos > Math.floor(total / 2)) {
                pos = pos - total
              }

              const isCenter = pos === 0
              const isAdjacent = Math.abs(pos) === 1
              const isEmerald = step.color === 'emerald'

              return (
                <div
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className="absolute w-64 h-80 md:w-72 md:h-96 transition-all duration-500 ease-in-out flex items-center justify-center cursor-pointer"
                  style={{
                    transform: `
                      translateX(${pos * 55}%)
                      scale(${isCenter ? 1 : isAdjacent ? 0.8 : 0.65})
                      rotateY(${pos * -12}deg)
                    `,
                    zIndex: isCenter ? 10 : isAdjacent ? 5 : 1,
                    opacity: isCenter ? 1 : isAdjacent ? 0.5 : 0,
                    filter: isCenter ? 'blur(0px)' : 'blur(3px)',
                    visibility: Math.abs(pos) > 1 ? 'hidden' : 'visible',
                  }}
                >
                  {/* Card */}
                  <div className={`relative w-full h-full rounded-3xl border-2 shadow-2xl overflow-hidden flex flex-col items-center justify-center p-6 text-center ${
                    index === 0
                      ? 'como-card-hero border-purple-400/50 shadow-purple-600/40'
                      : isEmerald
                        ? `bg-white dark:bg-neutral-900 ${isCenter ? 'border-emerald-500/50 shadow-emerald-500/20' : 'border-indigo-950/10 dark:border-white/10'}`
                        : `bg-white dark:bg-neutral-900 ${isCenter ? 'border-purple-500/50 shadow-purple-500/20' : 'border-indigo-950/10 dark:border-white/10'}`
                  }`}>
                    {/* Step number - only for last step */}
                    {index === 3 && (
                      <div className="w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-2xl text-white mb-4 bg-emerald-500">
                        <Check className="w-8 h-8" />
                      </div>
                    )}

                    {/* Metric */}
                    <div className={`text-3xl md:text-4xl font-bold mb-2 ${
                      index === 0
                        ? 'text-white'
                        : isEmerald
                          ? 'text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-emerald-600'
                          : 'text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-600'
                    }`}>
                      {step.metric}
                    </div>

                    {/* Title */}
                    <h3 className={`text-lg md:text-xl font-semibold mb-3 ${
                      index === 0 ? 'text-white' : 'text-indigo-950 dark:text-white'
                    }`}>
                      {step.title}
                    </h3>

                    {/* Description */}
                    <p className={`text-sm leading-relaxed mb-4 ${
                      index === 0 ? 'text-white/80' : 'text-indigo-950/70 dark:text-white/60'
                    }`}>
                      {step.description}
                    </p>

                    {/* Footer */}
                    {index === 0 ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          trackCtaClick(step.footer, 'como_funciona_card')
                          onStart()
                        }}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white text-purple-600 font-semibold text-sm hover:bg-white/90 transition-all shadow-lg hover:scale-105 active:scale-95"
                      >
                        {step.footer}
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <div className={`text-sm ${
                        isEmerald ? 'text-emerald-600 dark:text-emerald-400' : 'text-purple-600 dark:text-purple-400'
                      }`}>
                        {step.footer}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Navigation Buttons */}
          <button
            aria-label="Passo anterior"
            className="group absolute left-2 sm:left-8 top-1/2 -translate-y-1/2 rounded-full h-12 w-12 z-20 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm border border-indigo-950/10 dark:border-white/10 flex items-center justify-center hover:border-purple-500 hover:-translate-y-[calc(50%+2px)] active:translate-y-[calc(-50%+2px)] transition-all duration-200 shadow-lg"
            onClick={handlePrev}
          >
            <ArrowLeft className="h-5 w-5 text-indigo-950 dark:text-white group-hover:rotate-12 transition-transform duration-300" />
          </button>
          <button
            aria-label="Próximo passo"
            className="group absolute right-2 sm:right-8 top-1/2 -translate-y-1/2 rounded-full h-12 w-12 z-20 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm border border-indigo-950/10 dark:border-white/10 flex items-center justify-center hover:border-purple-500 hover:-translate-y-[calc(50%+2px)] active:translate-y-[calc(-50%+2px)] transition-all duration-200 shadow-lg"
            onClick={handleNext}
          >
            <ArrowRight className="h-5 w-5 text-indigo-950 dark:text-white group-hover:-rotate-12 transition-transform duration-300" />
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 mt-5 sm:mt-8" role="tablist" aria-label="Passos do processo">
          {processSteps.map((step, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Ir para passo ${index + 1}: ${step.title}`}
              aria-selected={index === currentIndex}
              role="tab"
              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? step.color === 'emerald' ? 'bg-emerald-500 w-6' : 'bg-purple-500 w-6'
                  : 'bg-indigo-950/20 dark:bg-white/20 hover:bg-purple-500/50'
              }`}
            />
          ))}
        </div>

        {/* CTA Button */}
        <div className="mt-6 pt-6 sm:mt-16 sm:pt-16 text-center">
          <button
            onClick={() => { trackCtaClick('Quero meu site em 7 dias', 'como_funciona_bottom'); onStart() }}
            className="cta-glow-purple group inline-flex items-center justify-center gap-3 px-8 py-4 sm:px-10 sm:py-5 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white text-base sm:text-lg font-semibold transition-all duration-300 active:scale-[0.98]"
          >
            Quero meu site em 7 dias
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </motion.section>
  )
}

function CountdownTimer() {
  const [ms, setMs] = useState(120000)
  const ref = useRef<HTMLSpanElement>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true)
    }, { threshold: 0.5 })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  useEffect(() => {
    if (!inView) return
    const id = setInterval(() => {
      setMs(prev => (prev <= 0 ? 120000 : prev - 37))
    }, 37)
    return () => clearInterval(id)
  }, [inView])

  const m = Math.floor(ms / 60000)
  const s = Math.floor((ms % 60000) / 1000)
  const c = Math.floor((ms % 1000) / 10)

  return (
    <span ref={ref} className="tabular-nums font-semibold text-purple-600 dark:text-purple-400">
      {m}:{s.toString().padStart(2, '0')}.{c.toString().padStart(2, '0')}
    </span>
  )
}

export function HeroLandingB({ onStart }: HeroLandingBProps) {
  const [showHeroContent, setShowHeroContent] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const headlineStartRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    setIsMobile(
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints > 0 && window.innerWidth < 768)
    )
  }, [])

  const handleExitStart = () => {
    setShowHeroContent(true)
  }

  return (
    <div className="min-h-[100dvh] w-full flex flex-col relative overflow-x-hidden bg-white dark:bg-black">
      {/* Background - CosmicShader */}
      <CosmicShader />

      {/* Content */}
      <div className="relative z-10">
        {/* Spacer for fixed header */}
        <div className="h-14 sm:h-16" />

        {/* Hero Section - Animação e conteúdo ocupam o mesmo espaço */}
        <div className="relative w-full min-h-[380px] sm:min-h-[500px] md:min-h-[550px] flex items-center justify-center overflow-visible">
          {/* Intro Animation - Dentro do hero */}
          <IntroAnimation
            images={animatedProjects.slice(0, 10).map(p => ({
              src: p.src,
              name: p.name,
            }))}
            onExitStart={handleExitStart}
          />

          {/* Hero Content - aparece quando animação começa a sair */}
          <div className="relative z-[55] w-full pt-2 sm:pt-12 pb-4 sm:pb-8 text-center px-4">
            {/* Headline */}
            <h1 className="mx-auto max-w-4xl text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight text-indigo-950 dark:text-white">
              <span ref={headlineStartRef}>
                <BlurTextEffect trigger={showHeroContent} delay={0.4}>
                  Seu site trabalhando
                </BlurTextEffect>
              </span>{' '}
              <BlurTextEffect trigger={showHeroContent} delay={0.7} className="text-purple-500 dark:text-purple-400">
                24h
              </BlurTextEffect>{' '}
              <BlurTextEffect trigger={showHeroContent} delay={0.85}>
                para trazer
              </BlurTextEffect>{' '}
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: showHeroContent ? 1 : 0 }}
                transition={{ delay: 1.0, duration: 0.4 }}
              >
                <TextLoop interval={2.5} compareRef={headlineStartRef} className="text-purple-500 dark:text-purple-400 inline-flex items-center">
                  <span>clientes</span>
                  <span>vendas</span>
                  <span>resultados</span>
                  <span>leads</span>
                  <span>dinheiro</span>
                  <span>sucesso</span>
                </TextLoop>
              </motion.span>
            </h1>

            {/* Subtitle */}
            <p className="mx-auto mt-4 sm:mt-6 max-w-2xl text-base sm:text-lg text-indigo-950/80 dark:text-gray-300">
              <BlurTextEffect trigger={showHeroContent} delay={1.1}>
                Criamos sites profissionais que colocam seu negócio na primeira página do Google.
              </BlurTextEffect>
            </p>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: showHeroContent ? 1 : 0,
                y: showHeroContent ? 0 : 20
              }}
              transition={{ delay: 1.4, duration: 0.5 }}
              className="mt-5 sm:mt-8"
            >
              <button
                onClick={() => { trackCtaClick('Quero meu site em 7 dias', 'hero_principal'); onStart() }}
                aria-label="Começar orçamento do site"
                className="cta-glow-purple group h-12 sm:h-16 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 px-10 sm:px-16 text-base sm:text-lg font-semibold text-white hover:from-purple-500 hover:to-purple-400 transition-all duration-300 flex items-center justify-center gap-3 mx-auto"
              >
                Quero meu site em 7 dias
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-5 mt-3 sm:mt-5 text-xs sm:text-sm">
                <span className="flex items-center gap-1.5 text-indigo-950/70 dark:text-white/60">
                  <svg className="w-4 h-4 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Leva só <CountdownTimer />
                </span>
                <span className="flex items-center gap-1.5 font-medium text-emerald-600 dark:text-emerald-400">
                  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75" />
                    <circle cx="12" cy="12" r="9" />
                  </svg>
                  Sem compromisso
                </span>
                <span className="flex items-center gap-1.5 text-purple-600 dark:text-purple-400 font-medium">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                  </span>
                  Resposta em 2 minutos
                </span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Section: Empresas que já faturam com a gente */}
        <section id="portfolio" className="relative z-[60] pt-0 pb-6 sm:pb-16 lg:pt-8 lg:pb-24 scroll-mt-20">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 px-6">
            {/* Badge */}
            <div className="inline-flex mb-3 sm:mb-5">
              <div className="border border-indigo-950/20 dark:border-white/20 py-1 px-4 rounded-full text-indigo-950/70 dark:text-white/60 text-sm backdrop-blur-sm">
                Portfólio
              </div>
            </div>

            {/* Title */}
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-indigo-950 dark:text-white mb-3 sm:mb-4">
              Sites que já estão{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-600 dark:from-purple-400 dark:to-purple-600">
                gerando resultados
              </span>
            </h2>

            {/* Description */}
            <p className="text-indigo-950/70 dark:text-white/60 text-base lg:text-lg leading-relaxed">
              Entregues com resultados comprovados
            </p>
          </div>
          <AnimatedTestimonials testimonials={animatedProjects} />

          {/* Trust points + Stats */}
          <motion.div
            initial={{ opacity: 0, y: isMobile ? 0 : 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: isMobile ? 0 : 0.2, duration: isMobile ? 0.3 : 0.5 }}
            className="mt-5 sm:mt-16 lg:mt-24 px-4"
          >
            {/* Trust points */}
            <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center sm:justify-center gap-4 sm:gap-6 text-sm">
              <div className="flex items-center gap-2 text-indigo-950/80 dark:text-white/70">
                <Check className="w-5 h-5 text-emerald-500" />
                <span>Online em <strong className="text-indigo-950 dark:text-white">7 dias</strong> ou devolvemos seu dinheiro</span>
              </div>
              <div className="flex items-center gap-2 text-indigo-950/80 dark:text-white/70">
                <Check className="w-5 h-5 text-emerald-500" />
                <span>Apareça na <strong className="text-indigo-950 dark:text-white">1ª página</strong> do Google</span>
              </div>
              <div className="flex items-center gap-2 text-indigo-950/80 dark:text-white/70">
                <Check className="w-5 h-5 text-emerald-500" />
                <span>Receba contatos pelo <strong className="text-indigo-950 dark:text-white">WhatsApp 24h</strong></span>
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap items-stretch justify-center gap-3 sm:gap-6 mt-6 sm:mt-10">
              <div className="flex flex-col items-center justify-center w-32 sm:w-36 h-24 bg-white/40 dark:bg-white/10 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-white/20 shadow-lg shadow-purple-500/5">
                <div className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-600">150+</div>
                <div className="text-[10px] text-indigo-950/70 dark:text-white/60 uppercase tracking-wider mt-1">sites entregues</div>
              </div>
              <div className="flex flex-col items-center justify-center w-32 sm:w-36 h-24 bg-white/40 dark:bg-white/10 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-white/20 shadow-lg shadow-purple-500/5">
                <div className="text-3xl sm:text-4xl font-bold text-indigo-950 dark:text-white flex items-center gap-1">
                  5.0 <span className="text-yellow-500">★</span>
                </div>
                <div className="text-[10px] text-indigo-950/70 dark:text-white/60 uppercase tracking-wider mt-1">avaliação Google</div>
              </div>
              <a
                href="https://www.google.com/partners/agency?id=8946336424"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ver certificação Google Partner"
                className="flex flex-col items-center justify-center w-32 sm:w-36 h-24 bg-white/40 dark:bg-white/10 backdrop-blur-xl rounded-2xl border border-white/50 dark:border-white/20 shadow-lg shadow-purple-500/5 hover:border-purple-500/50 hover:shadow-purple-500/10 transition-all"
              >
                <Image
                  src="/google-partner-badge.svg"
                  alt=""
                  width={36}
                  height={36}
                  sizes="36px"
                  className="h-9 w-9"
                  aria-hidden="true"
                />
                <div className="text-[10px] text-indigo-950/70 dark:text-white/60 uppercase tracking-wider mt-1">Google Partner</div>
              </a>
            </div>
          </motion.div>
        </section>

        {/* Section: Como funciona? */}
        <ComoFuncionaSection onStart={onStart} />

        {/* Section: O que nossos clientes dizem (Testimonials Columns) */}
        <section id="depoimentos" className="py-12 sm:py-20 relative scroll-mt-20">
          <div className="container mx-auto z-10">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: isMobile ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: isMobile ? 0.3 : 0.8, delay: isMobile ? 0 : 0.1, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 px-6"
            >
              {/* Badge */}
              <div className="inline-flex mb-3 sm:mb-5">
                <div className="border border-indigo-950/20 dark:border-white/20 py-1 px-4 rounded-full text-indigo-950/70 dark:text-white/60 text-sm backdrop-blur-sm">
                  Depoimentos
                </div>
              </div>

              {/* Title */}
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-indigo-950 dark:text-white mb-3 sm:mb-4">
                O que nossos{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-600 dark:from-purple-400 dark:to-purple-600">
                  clientes dizem
                </span>
              </h2>

              {/* Description */}
              <p className="text-indigo-950/70 dark:text-white/60 text-base lg:text-lg leading-relaxed">
                Avaliado com 5.0 estrelas no Google
              </p>
            </motion.div>

            {/* Columns with mask gradient */}
            <div className="flex justify-center gap-6 mt-6 sm:mt-10 [mask-image:linear-gradient(to_bottom,transparent,black_25%,black_75%,transparent)] max-h-[520px] sm:max-h-[740px] overflow-hidden">
              <TestimonialsColumn
                testimonials={firstColumn}
                duration={15}
              />
              <TestimonialsColumn
                testimonials={secondColumn}
                className="hidden md:block"
                duration={19}
              />
              <TestimonialsColumn
                testimonials={thirdColumn}
                className="hidden lg:block"
                duration={17}
              />
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-10 sm:py-16 lg:py-24">
          <div className="px-6 sm:px-10 lg:px-16">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-indigo-950 dark:text-white text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">
                Pronto para ter seu site?
              </h2>
              <p className="text-indigo-950/60 dark:text-white/50 text-base sm:text-lg mb-6 sm:mb-8">
                Comece agora e tenha seu site no ar em até 7 dias
              </p>
              <button
                onClick={() => { trackCtaClick('Quero meu site em 7 dias', 'cta_final'); onStart() }}
                aria-label="Começar orçamento do site"
                className="cta-glow-purple group w-full sm:w-auto px-10 py-5 sm:px-12 sm:py-6 rounded-full bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 text-white text-lg sm:text-xl font-semibold transition-all duration-300 active:scale-[0.98]"
              >
                <span className="flex items-center justify-center gap-3">
                  Quero meu site em 7 dias
                  <ArrowRight className="w-6 h-6 transition-transform group-hover:translate-x-1" />
                </span>
              </button>
              <div className="flex flex-wrap justify-center items-center gap-4 mt-4 sm:mt-6 text-xs sm:text-sm text-indigo-950/50 dark:text-white/40">
                <span className="flex items-center gap-1.5">
                  <Check className="w-4 h-4" />
                  2 minutos
                </span>
                <span>•</span>
                <span className="flex items-center gap-1.5">
                  <Check className="w-4 h-4" />
                  Sem compromisso
                </span>
                <span>•</span>
                <span>Garantia de 7 dias</span>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="w-full z-10 border-t border-purple-500/20 dark:border-white/10 bg-white/90 dark:bg-black/20 backdrop-blur-sm">
          <div className="px-6 sm:px-10 lg:px-16 py-6 sm:py-8">
            <div className="max-w-5xl mx-auto">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <div className="flex items-center gap-3">
                  <AnimatedBiomoLogo className="h-6 w-auto opacity-60" shimmer={false} />
                  <span className="text-indigo-950/70 dark:text-white/50 text-sm">
                    © {new Date().getFullYear()} Biomo
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <a
                    href="https://wa.me/5547996067992"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-950/70 dark:text-white/50 hover:opacity-80 transition-colors"
                  >
                    WhatsApp
                  </a>
                  <span className="text-indigo-950/30 dark:text-white/30">|</span>
                  <a href="https://biomo.com.br/privacidade" className="text-indigo-950/70 dark:text-white/50 hover:opacity-80 transition-colors">
                    Privacidade
                  </a>
                  <span className="text-indigo-950/30 dark:text-white/30">|</span>
                  <a href="https://biomo.com.br/termos" className="text-indigo-950/70 dark:text-white/50 hover:opacity-80 transition-colors">
                    Termos
                  </a>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-purple-500/10 dark:border-white/5">
                <p className="text-indigo-950/50 dark:text-white/30 text-xs text-center sm:text-left">
                  Biomo Marketing Digital LTDA · CNPJ: 48.423.613/0001-68 · Balneário Camboriú, SC
                </p>
              </div>
            </div>
          </div>
        </footer>

        {/* Floating Google Partner Badge - hidden on mobile/tablet */}
        <a
          href="https://www.google.com/partners/agency?id=8946336424"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Ver certificação Google Partner"
          className="hidden lg:block fixed bottom-6 right-6 z-[90] group"
        >
          <div className="flex flex-col items-center gap-4 bg-white/90 dark:bg-white/5 backdrop-blur-xl border border-purple-500/20 dark:border-white/10 rounded-2xl px-4 py-4 shadow-xl shadow-purple-500/10 group-hover:shadow-purple-500/20 group-hover:border-purple-500/40 group-hover:-translate-y-1 transition-all duration-300">
            <Image
              src="/google-partner-badge.svg"
              alt=""
              width={56}
              height={56}
              sizes="56px"
              className="h-14 w-14 object-contain"
              aria-hidden="true"
            />
            <span className="text-indigo-950 dark:text-white text-xs font-semibold">Google Partner</span>
          </div>
        </a>
      </div>
    </div>
  )
}
